import classnames from 'classnames';
import { Link } from 'components/router';
import { Text } from 'components/typography';
import { useIntl } from 'react-intl';

type PaginationProps = {
  className?: string;
  linkTemplate?: (index: number) => string;
  activeIndex: number;
  onChange?: (index: number, event?: React.SyntheticEvent<EventTarget>) => void;
  totalPageCount: number;
};

type ArrowDirection = 'left' | 'right';

type ItemComponent = {
  href?: string;
  type?: string;
  onClick?: (e: React.SyntheticEvent<EventTarget>) => void;
};

import ChevronIcon from 'icons/ChevronIcon';

const Pagination = (props: PaginationProps): React.ReactNode => {
  const intl = useIntl();

  const itemClassName = (page: number) => {
    return classnames('w-8 h-11 flex justify-around items-center', {
      'transition-colors duration-300 hover:text-gray-700 cursor-pointer':
        props.activeIndex !== page,
      'text-red-500 cursor-default': props.activeIndex === page
    });
  };

  // If linkTemplate is passed through the props
  // then the ItemComponent will be rendered as Link
  // otherwise it'll be rendered as button,
  // so the rendered page-numbers can be either links or buttons

  let ItemComponent;
  const itemProps: ItemComponent = {};

  if (!props.linkTemplate) {
    ItemComponent = 'button';
    itemProps.type = 'button';
  } else {
    ItemComponent = Link;
  }

  const renderArrow = (direction: ArrowDirection) => {
    const newPageIndex =
      direction === 'left' ? props.activeIndex - 1 : props.activeIndex + 1;
    if (props.linkTemplate) {
      itemProps.href = props.linkTemplate(newPageIndex);
    }
    if (props.onChange) {
      itemProps.onClick = (e: React.SyntheticEvent<EventTarget>) =>
        (direction === 'left' && props.activeIndex === 1) ||
        (direction === 'right' && props.activeIndex === props.totalPageCount)
          ? null
          : props.onChange(newPageIndex, e);
    }
    return (
      <ItemComponent
        {...itemProps}
        aria-label={
          direction === 'left'
            ? intl.formatMessage({
                defaultMessage: 'Previous page'
              })
            : intl.formatMessage({
                defaultMessage: 'Next page'
              })
        }
        className="flex size-full items-center justify-center rounded-full bg-white shadow-md transition-colors duration-300 hover:text-gray-700"
        tabIndex={0}
      >
        {direction === 'left' ? (
          <ChevronIcon variant="left" className="h-4 w-2" />
        ) : (
          <ChevronIcon variant="right" className="h-4 w-2" />
        )}
      </ItemComponent>
    );
  };

  const renderBlock = (start: number, end: number) => {
    const items = [];
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
    return items.map(item => {
      if (props.linkTemplate) {
        itemProps.href = props.linkTemplate(item);
      }
      if (props.onChange) {
        itemProps.onClick = (e: React.SyntheticEvent<EventTarget>) =>
          props.onChange(item, e);
      }

      return (
        <ItemComponent
          key={item}
          {...itemProps}
          tabIndex={props.activeIndex === item ? -1 : 0}
          onClick={(e: React.SyntheticEvent<EventTarget>) =>
            props.onChange(item, e)
          }
          aria-label={
            props.activeIndex === item
              ? intl.formatMessage(
                  {
                    defaultMessage: `Page {item}, current`
                  },
                  {
                    item
                  }
                )
              : intl.formatMessage(
                  {
                    defaultMessage: `Page {item}`
                  },
                  {
                    item
                  }
                )
          }
          disabled={props.activeIndex === item}
          className={itemClassName(item)}
        >
          <Text size="m">{item}</Text>
        </ItemComponent>
      );
    });
  };

  const renderSeparator = () => {
    return (
      <div className="flex h-11 w-8 items-center justify-around text-gray-500">
        <Text size="m">...</Text>
      </div>
    );
  };

  // This logic is used to determine which
  // block of page-numbers should be rendered
  // and the way they should be rendered(with or without separator).
  // The ternary operators define blocks and calculate
  // the starting and ending page-number of each block
  // which depends on which page or index is currently active.

  const start1 = 1;
  const end1 =
    props.activeIndex > 3 ? start1 : props.activeIndex === 3 ? 4 : start1 + 2;
  const start2 =
    props.activeIndex > 3
      ? props.activeIndex === 4
        ? 4
        : props.activeIndex - 1
      : 0;
  const end2 = props.activeIndex === 4 ? 6 : props.activeIndex + 1;
  const start3 =
    props.activeIndex > props.totalPageCount - 3
      ? props.activeIndex === props.totalPageCount - 2
        ? props.totalPageCount - 3
        : props.totalPageCount - 2
      : props.totalPageCount;
  const end3 = props.totalPageCount;

  return (
    <div
      className={`flex items-center justify-center text-black ${
        props.className || ''
      }`}
      role="navigation"
      aria-label="Pagination Navigation"
    >
      <div className="mr-2 h-8 w-8 md:mr-5">
        {props.activeIndex !== 1 && renderArrow('left')}
      </div>
      {props.totalPageCount > 7 ? (
        <>
          {renderBlock(start1, end1)}
          {renderSeparator()}
          {start2 > 0 && start3 > end2 && renderBlock(start2, end2)}
          {start2 > 0 && start3 > end2 && renderSeparator()}
          {renderBlock(start3, end3)}
        </>
      ) : (
        <>{renderBlock(start1, end3)}</>
      )}
      <div className="ml-2 h-8 w-8 md:ml-5">
        {props.activeIndex !== props.totalPageCount && renderArrow('right')}
      </div>
    </div>
  );
};

export default Pagination;
